import React, { useEffect, useState, useRef, useCallback } from 'react';
import './home.css';
import logoNihil from './assets/images/Portada-album.png';
import bandPhoto from './assets/images/Banda-1.png';
import genreBg from './assets/images/banda-2.png';
import heroVideo from './assets/images/Teaser1-video.mp4';
import songFile from './assets/song/SignificacionaMartillazos.mp3';
import single1 from './assets/images/single1.PNG';

const Home = () => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const audioRef = useRef(null);

    // Video release states
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [videoError, setVideoError] = useState(false);
    const [videoLoadProgress, setVideoLoadProgress] = useState(0);
    const [videoVisible, setVideoVisible] = useState(false);
    const [videoStarted, setVideoStarted] = useState(false);
    const releaseVideoRef = useRef(null);
    const releaseSectionRef = useRef(null);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const updateProgress = () => {
            const current = (audio.currentTime / audio.duration) * 100;
            setProgress(current || 0);
        };

        const onLoadedMetadata = () => {
            setDuration(audio.duration);
        };

        audio.addEventListener('timeupdate', updateProgress);
        audio.addEventListener('loadedmetadata', onLoadedMetadata);

        return () => {
            audio.removeEventListener('timeupdate', updateProgress);
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
        };
    }, []);

    const togglePlay = () => {
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleProgressChange = (e) => {
        const newTime = (e.target.value / 100) * audioRef.current.duration;
        audioRef.current.currentTime = newTime;
        setProgress(e.target.value);
    };

    const formatTime = (time) => {
        if (isNaN(time)) return "0:00";
        const mins = Math.floor(time / 60);
        const secs = Math.floor(time % 60);
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    useEffect(() => {
        const observerOptions = {
            threshold: 0.1
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                }
            });
        }, observerOptions);

        const sections = document.querySelectorAll('.section');
        sections.forEach(section => observer.observe(section));

        return () => observer.disconnect();
    }, []);

    // Lazy video load: observe release section & start loading when visible
    useEffect(() => {
        const sectionEl = releaseSectionRef.current;
        if (!sectionEl) return;

        const videoObserver = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting && !videoStarted) {
                    setVideoVisible(true);
                    setVideoStarted(true);
                    // Start loading video src dynamically
                    const vid = releaseVideoRef.current;
                    if (vid && !vid.src) {
                        vid.src = '/REEL FANTASIA.mp4';
                        vid.load();
                    }
                }
            },
            { threshold: 0.15 }
        );

        videoObserver.observe(sectionEl);
        return () => videoObserver.disconnect();
    }, [videoStarted]);

    // Track buffering progress
    const handleVideoProgress = useCallback(() => {
        const vid = releaseVideoRef.current;
        if (!vid || vid.duration === 0) return;
        const buffered = vid.buffered;
        if (buffered.length > 0) {
            const pct = (buffered.end(buffered.length - 1) / vid.duration) * 100;
            setVideoLoadProgress(Math.round(pct));
        }
    }, []);

    const handleVideoCanPlay = useCallback(() => {
        setVideoLoaded(true);
    }, []);

    const handleVideoError = useCallback(() => {
        setVideoError(true);
    }, []);

    return (
        <div className="home-container">
            <nav className="navbar">
                <div className="nav-logo">NIHIL 13</div>
                <button className={`menu-toggle ${isMenuOpen ? 'open' : ''}`} onClick={() => setIsMenuOpen(!isMenuOpen)}>
                    <span></span>
                    <span></span>
                    <span></span>
                </button>
                <ul className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
                    <li><a href="#hero" onClick={() => setIsMenuOpen(false)}>ID</a></li>
                    <li><a href="#bio" onClick={() => setIsMenuOpen(false)}>BIO</a></li>
                    <li><a href="#nuevo-lanzamiento" onClick={() => setIsMenuOpen(false)} className="nav-release-link">🔥 NUEVO</a></li>
                    <li><a href="#genre" onClick={() => setIsMenuOpen(false)}>GENRE</a></li>
                    <li><a href="#disco" onClick={() => setIsMenuOpen(false)}>DISCO</a></li>
                    <li><a href="#contact" onClick={() => setIsMenuOpen(false)}>CONTACT</a></li>
                </ul>
            </nav>

            <section id="hero" className="section hero">
                <div className="video-background">
                    <video autoPlay loop muted playsInline className="hero-video">
                        <source src={heroVideo} type="video/mp4" />
                    </video>
                </div>
                <div className="hero-overlay"></div>
                <div className="hero-content miniaturized">
                    <div className="hero-branding-box">
                        <div className="hero-info">
                            <p className="tagline-mini">Nihil 13 - Significación a Martillazos</p>
                            <div className="audio-player-custom">
                                <audio ref={audioRef} src={songFile} loop />
                                <button onClick={togglePlay} className="play-pause-btn">
                                    {isPlaying ? (
                                        <span className="pause-icon">II</span>
                                    ) : (
                                        <span className="play-icon">▶</span>
                                    )}
                                </button>
                                <div className="player-controls-group">
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={progress}
                                        onChange={handleProgressChange}
                                        className="progress-slider"
                                    />
                                    <div className="time-display">
                                        {formatTime(audioRef.current?.currentTime)} / {formatTime(duration)}
                                    </div>
                                </div>
                                <a href="https://open.spotify.com/intl-es/track/7jWR4cSDo6HyqPeEl7MPXx?si=0724d13fa9c24b45" target="_blank" rel="noopener noreferrer" className="spotify-hero-link" title="Escuchar en Spotify">
                                    <svg viewBox="0 0 24 24" className="spotify-icon-svg">
                                        <path fill="currentColor" d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.503 17.31c-.223.364-.698.475-1.062.253-2.91-1.778-6.573-2.18-10.887-1.196-.414.095-.828-.168-.923-.582-.095-.414.168-.828.582-.923 4.73-1.082 8.767-.624 12.037 1.373.364.223.475.698.253 1.075zm1.474-3.264c-.28.455-.873.606-1.328.326-3.328-2.046-8.403-2.643-12.336-1.448-.512.155-1.04-.144-1.194-.656-.154-.512.144-1.04.656-1.194 4.512-1.37 10.12-.693 13.978 1.676.455.28.606.873.324 1.296zm.126-3.418C15.42 8.488 9.3 8.286 5.733 9.37c-.6.182-1.23-.162-1.413-.762-.182-.6.162-1.23.762-1.413 4.102-1.25 10.854-1.01 15.116 1.52.54.32.716 1.014.396 1.554-.32.54-1.014.716-1.554.401z" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="bio" className="section bio reveal">
                <div className="bio-container">
                    <div className="bio-image-wrapper">
                        <img src={bandPhoto} alt="NIHIL 13 Band" className="bio-image" />
                    </div>
                    <div className="bio-text">
                        <div className="section-title graffiti">Bio</div>
                        <p>
                            Fundada en 2023 en Cuenca-Ec. Nihil 13 es la fusión del metalcore, trap y otros subgéneros de metal extremo, con líricas poderosas e introspectivas.
                        </p>
                        <p className="bio-description">
                            Como resultado: una propuesta innovadora en la escena emergente ecuatoriana.
                        </p>
                    </div>
                </div>
            </section>

            {/* ===== NUEVO LANZAMIENTO ===== */}
            <section
                id="nuevo-lanzamiento"
                className="section release-section reveal"
                ref={releaseSectionRef}
            >
                {/* Background noise texture overlay */}
                <div className="release-bg-noise" />

                {/* Header badge */}
                <div className="release-badge-wrapper">
                    <span className="release-badge">🔥 NUEVO LANZAMIENTO</span>
                </div>

                {/* Main title */}
                <div className="release-title-block">
                    <h2 className="release-main-title graffiti">FANTASÍA VISCERAL</h2>
                    <p className="release-subtitle">Video Clip Oficial — 2026</p>
                    <div className="release-divider"><span /></div>
                </div>

                {/* Video container */}
                <div className={`release-video-wrapper ${videoVisible ? 'visible' : ''}`}>
                    {/* Loading overlay */}
                    {!videoLoaded && !videoError && videoStarted && (
                        <div className="video-loading-overlay">
                            <div className="loading-spinner" />
                            <p className="loading-text">
                                Cargando video...
                                {videoLoadProgress > 0 && (
                                    <span className="load-pct"> {videoLoadProgress}%</span>
                                )}
                            </p>
                            <div className="loading-bar-track">
                                <div
                                    className="loading-bar-fill"
                                    style={{ width: `${videoLoadProgress}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Placeholder when section not visible yet */}
                    {!videoStarted && (
                        <div className="video-placeholder">
                            <div className="placeholder-icon">▶</div>
                            <p>FANTASÍA VISCERAL</p>
                        </div>
                    )}

                    {/* Error fallback */}
                    {videoError && (
                        <div className="video-error-overlay">
                            <p>⚠️ Error al cargar el video</p>
                            <a
                                href="https://www.youtube.com/watch?v=hDePdLwiHFg&list=RDhDePdLwiHFg&start_radio=1"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="release-cta-btn"
                            >
                                Ver en YouTube ↗
                            </a>
                        </div>
                    )}

                    {/* The actual video — src injected by JS when in viewport */}
                    <video
                        ref={releaseVideoRef}
                        className={`release-video ${videoLoaded ? 'loaded' : ''}`}
                        loop
                        muted
                        playsInline
                        autoPlay
                        onProgress={handleVideoProgress}
                        onCanPlay={handleVideoCanPlay}
                        onError={handleVideoError}
                        preload="none"
                    />

                    {/* Hover glass overlay */}
                    <div className="release-video-hover-overlay">
                        <div className="hover-content">
                            <span className="hover-play-icon">▶</span>
                            <span className="hover-label">FANTASÍA VISCERAL</span>
                        </div>
                    </div>
                </div>

                {/* CTA button */}
                <div className="release-cta-wrapper">
                    <a
                        href="https://www.youtube.com/watch?v=hDePdLwiHFg&list=RDhDePdLwiHFg&start_radio=1"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="release-cta-btn"
                        id="release-youtube-btn"
                    >
                        <span className="cta-icon">▶</span>
                        <span>¡VE AL LANZAMIENTO YA!</span>
                        <span className="cta-arrow">↗</span>
                    </a>
                    <p className="release-cta-hint">Disponible en YouTube — NIHIL 13</p>
                </div>
            </section>

            <section id="genre" className="section genre reveal" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${genreBg})` }}>
                <div className="venue-overlay">
                    <div className="genre-title graffiti">Nu-Wave Metalcore</div>
                    <p className="genre-desc">
                        Una mezcla explosiva de riffs pesados, ritmos de trap y una atmósfera oscura que define la nueva era del metal ecuatoriano.
                    </p>
                </div>
            </section>

            <section id="disco" className="section disco reveal">
                <div className="section-header">
                    <div className="section-title">SINGLE</div>
                    <div className="section-subtitle">¡YA DISPONIBLE!</div>
                    <div className="line-divider"></div>
                </div>
                <div className="disco-grid single-card">
                    <a href="https://open.spotify.com/intl-es/track/7jWR4cSDo6HyqPeEl7MPXx?si=0724d13fa9c24b45" target="_blank" rel="noopener noreferrer" className="album-card spotify-link" style={{ backgroundImage: `url(${single1})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                        <div className="album-overlay"></div>
                        <div className="album-info">
                            <h3>Significación a Martillazos</h3>
                            <p>Single - 2026</p>
                            <div className="spotify-button">
                                <span className="platform-tag">Escuchar en Spotify</span>
                            </div>
                        </div>
                    </a>
                </div>
            </section>

            <footer id="contact" className="footer">
                <div className="footer-content">
                    <div className="contact-info">
                        <p>Email: <a href="mailto:nihil.13.nihil@gmail.com">nihil.13.nihil@gmail.com</a></p>
                        <p>WhatsApp: <a href="https://wa.me/593998988833">0998988833</a></p>
                        <p>Instagram: <a href="https://instagram.com/nihil.13.nihil">@nihil.13.nihil</a></p>
                    </div>
                    <div className="social-icons">
                        <a href="https://www.facebook.com/share/14bq4yTjVn1/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="social-icon facebook" title="Facebook">
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1V12h3v3h-3v6.8c4.56-.93 8-4.96 8-9.8z"/></svg>
                        </a>
                        <a href="https://youtube.com/@nihil13nihil?si=0D7WoP3vRu7i9ciA" target="_blank" rel="noopener noreferrer" className="social-icon youtube" title="YouTube">
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                        </a>
                        <a href="https://www.tiktok.com/@nihil.13.nihil?_r=1&_t=ZS-95ZeiGQBvG4" target="_blank" rel="noopener noreferrer" className="social-icon tiktok" title="TikTok">
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.17-2.86-.6-4.12-1.31a8.386 8.386 0 0 1-1.2-1.22c0 3.33-.01 6.66 0 9.99A8.196 8.196 0 0 1 12 23.97a8.196 8.196 0 0 1-5.11-1.68 8.191 8.191 0 0 1-1.61-2.1c-.81-1.55-1.07-3.32-.78-5.04a8.118 8.118 0 0 1 1.1-3C6.73 10.61 8.41 9.53 10.2 9.17c.4-.08.8-.13 1.2-.14.01 1.44-.01 2.87 0 4.3-.09.01-.18.02-.27.03a3.869 3.869 0 0 0-2.31 1.11c-.69.69-1.03 1.66-.94 2.63a3.89 3.89 0 0 0 1.34 2.68 3.911 3.911 0 0 0 2.68 1 3.911 3.911 0 0 0 2.68-1 3.905 3.905 0 0 0 1.22-3.14V.02z"/></svg>
                        </a>
                    </div>
                    <div className="footer-bottom">
                        &copy; 2026 NIHIL 13. All rights reserved | Web Development by <a href="https://sec-hat.com" target="_blank" rel="noopener noreferrer" className="dev-link">Sec-hat.com</a>.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Home;
